// Cliques, communities & components — FXC-8538 (Warp).
//
// "Show how cliques work for any given run/data/whatever." These operate
// on the undirected projection of the canonical graph, so they work for
// the spool knowledge graph, a provenance/atomic run, or any other data.
//
// Three lenses, increasingly strict:
//   - connectedComponents — who is reachable from whom at all.
//   - communities         — soft clusters via the Louvain method
//                           (modularity; dependency-free, deterministic).
//   - maximalCliques      — true cliques (every member adjacent to every
//                           other) via Bron–Kerbosch with pivoting.
//   - atomicUnits         — provenance-specific: an operation/case node
//                           plus the artifacts it consumes and produces.
//
// Cliques/communities are exponential in the worst case, so run these on
// a *bounded* slice (the revealed neighbourhood), never the millions-node
// backing graph.

import type { Graph, GraphEdge, GraphNode } from "./types";

/** A partition of nodes into clusters. */
export interface Clustering {
  /** node id → cluster id (0-based, compact). */
  labelOf: Map<string, number>;
  /** cluster id → member node ids. */
  clusters: Map<number, string[]>;
  /** number of clusters. */
  count: number;
}

/** Undirected adjacency (cliques/communities ignore edge direction). */
export function undirectedAdjacency(graph: Graph): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  for (const n of graph.nodes) adj.set(n.id, new Set());
  for (const e of graph.edges) {
    if (e.source === e.target) continue;
    adj.get(e.source)?.add(e.target);
    adj.get(e.target)?.add(e.source);
  }
  return adj;
}

/** Weakly-connected components — undirected reachability. */
export function connectedComponents(graph: Graph): Clustering {
  const adj = undirectedAdjacency(graph);
  const labelOf = new Map<string, number>();
  let next = 0;
  for (const n of graph.nodes) {
    if (labelOf.has(n.id)) continue;
    const id = next++;
    const stack = [n.id];
    labelOf.set(n.id, id);
    while (stack.length) {
      const cur = stack.pop()!;
      for (const nb of adj.get(cur) ?? []) {
        if (!labelOf.has(nb)) {
          labelOf.set(nb, id);
          stack.push(nb);
        }
      }
    }
  }
  return finalize(labelOf);
}

export interface CommunityOptions {
  /** Max local-move passes per Louvain level. Default 20. */
  maxIterations?: number;
  /** Resolution — higher = more, smaller communities. Default 1. */
  resolution?: number;
}

/**
 * Communities via the Louvain method (modularity maximisation): greedily
 * move nodes to neighbouring communities for the best modularity gain,
 * then aggregate each community into a super-node and repeat. Robust on
 * small bridged graphs where label propagation collapses everything into
 * one. Deterministic for a given node order (ties prefer the current
 * community, then the lowest id).
 */
export function communities(graph: Graph, opts: CommunityOptions = {}): Clustering {
  const { maxIterations = 20, resolution = 1 } = opts;
  const ids = graph.nodes.map((n) => n.id);
  const n0 = ids.length;
  if (n0 === 0) return finalize(new Map());

  const indexOf = new Map(ids.map((id, i) => [id, i] as const));

  // Level-0 weighted undirected adjacency (no self-loops yet).
  let adj: Map<number, number>[] = Array.from({ length: n0 }, () => new Map());
  let self: number[] = new Array(n0).fill(0);
  for (const e of graph.edges) {
    if (e.source === e.target) continue;
    const a = indexOf.get(e.source);
    const b = indexOf.get(e.target);
    if (a === undefined || b === undefined) continue;
    adj[a]!.set(b, (adj[a]!.get(b) ?? 0) + 1);
    adj[b]!.set(a, (adj[b]!.get(a) ?? 0) + 1);
  }

  // partition[originalIndex] → current super-node index.
  let partition = ids.map((_, i) => i);

  for (;;) {
    const n = adj.length;
    const comm = oneLevel(n, adj, self, resolution, maxIterations);
    const ncomm = compact(comm);
    if (ncomm === n) break; // no merging happened — converged.

    // Fold the level's assignment into the original-node partition.
    partition = partition.map((sn) => comm[sn]!);

    // Aggregate communities into super-nodes for the next level.
    const newAdj: Map<number, number>[] = Array.from({ length: ncomm }, () => new Map());
    const newSelf: number[] = new Array(ncomm).fill(0);
    for (let i = 0; i < n; i++) {
      const ci = comm[i]!;
      newSelf[ci]! += self[i]!;
      for (const [j, w] of adj[i]!) {
        if (j < i) continue; // count each undirected pair once
        const cj = comm[j]!;
        if (ci === cj) newSelf[ci]! += w;
        else {
          newAdj[ci]!.set(cj, (newAdj[ci]!.get(cj) ?? 0) + w);
          newAdj[cj]!.set(ci, (newAdj[cj]!.get(ci) ?? 0) + w);
        }
      }
    }
    adj = newAdj;
    self = newSelf;
    if (ncomm === 1) break;
  }

  const labelOf = new Map<string, number>();
  ids.forEach((id, i) => labelOf.set(id, partition[i]!));
  return finalize(labelOf);
}

/** One Louvain level: local moving until stable. Returns comm[node]. */
function oneLevel(
  n: number,
  adj: Map<number, number>[],
  self: number[],
  resolution: number,
  maxIterations: number,
): number[] {
  const deg = new Array(n).fill(0);
  let twoM = 0;
  for (let i = 0; i < n; i++) {
    let d = 2 * self[i]!;
    for (const w of adj[i]!.values()) d += w;
    deg[i] = d;
    twoM += d;
  }
  const comm = Array.from({ length: n }, (_, i) => i);
  const commTot = deg.slice(); // Σtot per community
  if (twoM === 0) return comm;

  for (let pass = 0; pass < maxIterations; pass++) {
    let moved = false;
    for (let i = 0; i < n; i++) {
      const ki = deg[i]!;
      const cur = comm[i]!;
      // Weight from i to each neighbouring community.
      const toComm = new Map<number, number>();
      for (const [j, w] of adj[i]!) toComm.set(comm[j]!, (toComm.get(comm[j]!) ?? 0) + w);

      // Remove i from its community.
      commTot[cur]! -= ki;

      let best = cur;
      let bestGain = (toComm.get(cur) ?? 0) - (resolution * commTot[cur]! * ki) / twoM;
      for (const [c, wTo] of toComm) {
        if (c === cur) continue;
        const gain = wTo - (resolution * commTot[c]! * ki) / twoM;
        if (gain > bestGain || (gain === bestGain && c < best)) {
          best = c;
          bestGain = gain;
        }
      }
      commTot[best]! += ki;
      if (best !== cur) {
        comm[i] = best;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return comm;
}

/** Compact community ids in `comm` to 0..k-1 (in first-seen order). Returns k. */
function compact(comm: number[]): number {
  const remap = new Map<number, number>();
  for (let i = 0; i < comm.length; i++) {
    const c = comm[i]!;
    let r = remap.get(c);
    if (r === undefined) {
      r = remap.size;
      remap.set(c, r);
    }
    comm[i] = r;
  }
  return remap.size;
}

export interface CliqueOptions {
  /** Ignore cliques smaller than this. Default 3. */
  minSize?: number;
  /** Safety cap on cliques returned (Bron–Kerbosch is exponential). Default 5000. */
  limit?: number;
}

/**
 * All maximal cliques (size ≥ minSize) via Bron–Kerbosch with pivoting.
 * A clique is a set of nodes all mutually adjacent. Returned largest-first.
 */
export function maximalCliques(graph: Graph, opts: CliqueOptions = {}): string[][] {
  const { minSize = 3, limit = 5000 } = opts;
  const adj = undirectedAdjacency(graph);
  const out: string[][] = [];

  const bk = (R: string[], P: Set<string>, X: Set<string>): void => {
    if (out.length >= limit) return;
    if (P.size === 0 && X.size === 0) {
      if (R.length >= minSize) out.push([...R]);
      return;
    }
    // Pivot: a vertex in P ∪ X with the most neighbours in P.
    let pivot: string | undefined;
    let best = -1;
    for (const u of [...P, ...X]) {
      let c = 0;
      const ua = adj.get(u)!;
      for (const v of P) if (ua.has(v)) c++;
      if (c > best) {
        best = c;
        pivot = u;
      }
    }
    const pivotAdj = pivot ? adj.get(pivot)! : new Set<string>();
    const candidates = [...P].filter((v) => !pivotAdj.has(v));
    for (const v of candidates) {
      const va = adj.get(v)!;
      bk(
        [...R, v],
        new Set([...P].filter((p) => va.has(p))),
        new Set([...X].filter((x) => va.has(x))),
      );
      P.delete(v);
      X.add(v);
      if (out.length >= limit) return;
    }
  };

  bk([], new Set(graph.nodes.map((n) => n.id)), new Set());
  out.sort((a, b) => b.length - a.length);
  return out;
}

export interface AtomicUnitOptions {
  /** Node types treated as the "operation" anchor of a unit. */
  operationTypes?: string[];
}

/**
 * Provenance "atomic units": each operation/case node plus the artifacts
 * directly wired to it (inputs + outputs). The most natural cluster in a
 * provenance DAG — e.g. a solve case together with CL, CD, … and its
 * mesh. Artifacts shared by several operations are assigned to the first
 * unit that claims them; orphan artifacts form singleton clusters.
 */
export function atomicUnits(graph: Graph, opts: AtomicUnitOptions = {}): Clustering {
  const operationTypes = new Set(
    opts.operationTypes ?? ["operation", "case", "solve"],
  );
  const adj = undirectedAdjacency(graph);
  const labelOf = new Map<string, number>();
  let next = 0;
  for (const n of graph.nodes) {
    if (!operationTypes.has(n.type) || labelOf.has(n.id)) continue;
    const id = next++;
    labelOf.set(n.id, id);
    for (const nb of adj.get(n.id) ?? []) {
      if (!labelOf.has(nb)) labelOf.set(nb, id);
    }
  }
  // Anything not pulled into a unit becomes its own singleton.
  for (const n of graph.nodes) if (!labelOf.has(n.id)) labelOf.set(n.id, next++);
  return finalize(labelOf);
}

export interface CollapseOptions {
  /** Which clusters to collapse. Omit to collapse every cluster. */
  collapse?: Set<number>;
  /** Super-node id prefix. Default "cluster:". */
  idPrefix?: string;
  /** Label for a super-node given its members. Default "N · dominantType". */
  superLabel?: (clusterId: number, members: string[], dominantType: string) => string;
}

/**
 * Collapse clusters into single super-nodes: members of a collapsed
 * cluster become one node, edges crossing into/out of it re-route to the
 * super-node (internal edges dropped, parallel edges deduped). Nodes in
 * non-collapsed clusters pass through unchanged. Pure — returns a new
 * graph. Useful for a structural overview ("this run is 40 cases, each a
 * cluster of its scalars") with drill-down by expanding a super-node.
 */
export function collapseClusters(
  graph: Graph,
  clustering: Clustering,
  opts: CollapseOptions = {},
): Graph {
  const { collapse, idPrefix = "cluster:", superLabel } = opts;
  const typeOf = new Map(graph.nodes.map((n) => [n.id, n.type] as const));
  const isCollapsed = (cid: number) => (collapse ? collapse.has(cid) : true);

  // Map each node id to its rendered id (super-node when collapsed).
  const superIdOf = new Map<string, string>();
  for (const [cid, members] of clustering.clusters) {
    if (!isCollapsed(cid)) continue;
    const sid = `${idPrefix}${cid}`;
    for (const m of members) superIdOf.set(m, sid);
  }
  const renderId = (id: string) => superIdOf.get(id) ?? id;

  // Build super-nodes + pass-through nodes.
  const nodes: GraphNode[] = [];
  const seen = new Set<string>();
  for (const n of graph.nodes) {
    const cid = clustering.labelOf.get(n.id);
    if (cid !== undefined && isCollapsed(cid)) {
      const sid = `${idPrefix}${cid}`;
      if (seen.has(sid)) continue;
      seen.add(sid);
      const members = clustering.clusters.get(cid) ?? [];
      const dom = dominantTypeOf(members, typeOf);
      nodes.push({
        id: sid,
        type: "cluster",
        label: superLabel ? superLabel(cid, members, dom) : `${members.length} · ${dom}`,
        data: { clusterId: cid, size: members.length, dominantType: dom, collapsed: true },
      });
    } else {
      nodes.push(n);
    }
  }

  // Re-route + dedup edges; drop edges internal to a collapsed cluster.
  const edges: GraphEdge[] = [];
  const edgeSeen = new Set<string>();
  for (const e of graph.edges) {
    const s = renderId(e.source);
    const t = renderId(e.target);
    if (s === t) continue; // internal to a collapsed cluster
    const key = JSON.stringify([s, t, e.predicate]);
    if (edgeSeen.has(key)) continue;
    edgeSeen.add(key);
    edges.push({ source: s, target: t, predicate: e.predicate });
  }

  return {
    nodes,
    edges,
    nodeTypes: distinctStr(nodes.map((n) => n.type)),
    edgePredicates: distinctStr(edges.map((e) => e.predicate)),
  };
}

function dominantTypeOf(members: string[], typeOf: Map<string, string>): string {
  const counts = new Map<string, number>();
  let best = "";
  let bestC = 0;
  for (const m of members) {
    const t = typeOf.get(m);
    if (!t) continue;
    const c = (counts.get(t) ?? 0) + 1;
    counts.set(t, c);
    if (c > bestC) {
      best = t;
      bestC = c;
    }
  }
  return best;
}

function distinctStr(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) if (!seen.has(v)) { seen.add(v); out.push(v); }
  return out;
}

function finalize(raw: Map<string, number>): Clustering {
  // Compact arbitrary label ints into 0..k-1, stable by first appearance.
  const remap = new Map<number, number>();
  const labelOf = new Map<string, number>();
  const clusters = new Map<number, string[]>();
  for (const [id, l] of raw) {
    let c = remap.get(l);
    if (c === undefined) {
      c = remap.size;
      remap.set(l, c);
      clusters.set(c, []);
    }
    labelOf.set(id, c);
    clusters.get(c)!.push(id);
  }
  return { labelOf, clusters, count: clusters.size };
}
